import React from 'react';
import styled from 'styled-components';

import ToggleSidebarIcon from '../../svgs/ToggleSidebarIcon';
import { SidebarTooltip } from '../SidebarTooltip';

type FloatingToolPanelProps = {
  /**
   * Names the active tool. Rendered visibly unless `breadcrumbs` supersedes it;
   * still required either way, so call sites always state which tool the card
   * belongs to.
   */
  title: string;
  /**
   * Replaces the visible title when the tool has drilled into a specific
   * element — the trail's own first item already names the tool ("Media"), so
   * rendering both would just say it twice.
   */
  breadcrumbs?: React.ReactNode;
  /**
   * Inline control that belongs beside the title rather than in the body — the
   * captions panel's language toggle. Packs left with the title so the close
   * button stays opposite the pair.
   */
  titleAdornment?: React.ReactNode;
  /**
   * Loading overlay for the body (a `SpinningLoading`, which positions itself
   * absolutely — hence `position: relative` on `Body`). Rendered above the
   * content rather than in place of it, matching the full-height shell.
   */
  loading?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /**
   * Panel-level navigation (the tool's sub-tab row), pinned below the header so
   * it survives scrolling the body. A capped-height card scrolls far more often
   * than the full-height shell did, which is what makes pinning this worth a
   * slot of its own.
   */
  subheader?: React.ReactNode;
  /** Attached to the scrolling body, for consumers that scroll it. */
  bodyRef?: React.RefObject<HTMLDivElement>;
  /**
   * Lands on the card's outermost element, so `getElementById` lookups treat the
   * whole card as one region. The audio panel relies on this: the timeline's
   * volume-keypoint click-away handler exempts `#audio-settings-wrapper` so that
   * adjusting volume in the panel doesn't deselect the keypoint. Scoping it to
   * the body instead would let a click on the header drop the selection.
   */
  id?: string;
};

/**
 * Floating-card shell for a right-rail tool panel (PLAT-11087).
 *
 * The alternative shell — `PanelContent` in `RightSidePanel` — fills the full
 * height of the workspace, squares off against the screen edges and welds
 * itself to the rail, so every tool reads as a second wall of the app rather
 * than as something the user just reached for. This shell instead presents the
 * same content as a card: inset from the workspace edges, rounded, elevated,
 * and only as tall as its content needs (see `ExpandablePanel`'s floating
 * variant, which owns the card's box).
 *
 * Two consequences for content rendered inside:
 * - The header is fixed and the body scrolls, so children must not bring their
 *   own absolutely-positioned full-height scroll area (the `ScrollableArea`
 *   wrapper the full-height shell requires). A child's `height: 100%` resolves
 *   to `auto` against this indefinite-height body and simply lets the body
 *   scroll instead — which is what we want, one scrollbar.
 * - The title is visible here. The full-height shell hides it because the rail
 *   label already names the section directly alongside it; a detached card has
 *   to identify itself.
 */
export const FloatingToolPanel: React.FC<FloatingToolPanelProps> = ({
  title,
  breadcrumbs,
  titleAdornment,
  loading,
  onClose,
  children,
  subheader,
  bodyRef,
  id,
}) => (
  // Panel inputs must not leak keystrokes to the app's global shortcut
  // handlers (matches `PanelContentContainer`).
  <Card id={id} onKeyDown={e => e.stopPropagation()}>
    <Header>
      {breadcrumbs ?? (
        <TitleGroup>
          <Title>{title}</Title>
          {titleAdornment}
        </TitleGroup>
      )}
      <CloseButton onClick={onClose} aria-label="Close sidebar">
        <ToggleSidebarIcon />
        <CloseTooltip>Close sidebar</CloseTooltip>
      </CloseButton>
    </Header>
    {subheader && <SubHeader>{subheader}</SubHeader>}
    <Body ref={bodyRef}>
      {loading}
      {children}
    </Body>
  </Card>
);

export default FloatingToolPanel;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  /* Shrink to the height its parent allows once content outgrows it, so the
     body — not the page — is what scrolls. */
  min-height: 0;
  max-height: 100%;
  /* --background, the same fill the full-height shell used, and deliberately not
     a step up the ramp.
     The card has to sit BELOW everything it contains: the depth ladder assigns
     --surface to selectable items (theme options, duration buttons, template
     cards) and --input-background (also gray-800) to fields, so any lighter card
     fill collides with one or the other. --surface was the worst case — byte
     identical to the items — but --surface-subtle is no better in practice.
     Arbor's dark ramp spans #0e0e10 to #202226 across four steps, so adjacent
     fills differ by ~1.05-1.12 contrast: measurably distinct, visually not.
     Fills cannot carry hierarchy in this palette. At --background the items keep
     their best available step (1.120 vs 1.068 on a gray-800 card), and the
     card's border plus --elevation-lg do the floating — which is what actually
     reads, and how the old shell worked. */
  background-color: var(--background);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-card);
  box-shadow: var(--elevation-lg);
  /* Clip content to the rounded corners. */
  overflow: hidden;
  box-sizing: border-box;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm)
    var(--spacing-md);
  border-bottom: 1px solid var(--border-subtle);
  flex-shrink: 0;
`;

/** Keeps the title and its adornment packed left, opposite the close button. */
const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-sm);
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Opens to the left — the button sits at the card's right edge. */
const CloseTooltip = styled(SidebarTooltip)`
  left: auto;
  right: calc(100% + 8px);
`;

const CloseButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:hover ${CloseTooltip} {
    opacity: 1;
  }

  /* Steps back down from the hover — "pressed = less elevated". */
  &:active {
    background-color: var(--surface);
    transform: scale(0.95);
  }
`;

/**
 * Horizontal padding only: the sub-tab row brings its own vertical rhythm and
 * its own bottom rule, and renders nothing at all for tools whose sub-tabs are
 * flagged off — which must collapse to zero height, not leave a gap.
 */
const SubHeader = styled.div`
  padding: 0 var(--spacing-md);
  flex-shrink: 0;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Positioning context for the absolutely-positioned loading overlay — the
     full-height shell got this from its own scroll container. */
  position: relative;
  /* Matches the horizontal padding of the full-height shell so panel content
     lands at the same inset either way. */
  padding: 0 var(--spacing-md) var(--spacing-md);
`;
